import { FC, useState, useReducer, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'

// Redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { fetchSlipsListAction } from '../../../store/slips'
import { editBillResetAction, updateBillAction } from '../../../store/items'
import { showAlertAction } from '../../../store/alerts'
import {
  fetchCustomerAccountAction,
  getAgreementsAction,
} from '../../../store/customerAccounts'
import { fetchCustomersActionV2 } from '../../../store/customers'
import {
  formatPartyAutocompleteLabel,
  formatPartyOptionSecondaryLabel,
  formatPartySelectedLabel,
  getFormattedDateTimeIST,
  isValidGST,
} from '../../../utils'
import { Agreement, Bill, Customer } from 'src/services'
import CustomNumberField from '../Users/profile/CustomNumberField'
import SelectStateItem, { getState, StateOption } from '../Customers/SelectStateItem'
import { BillType, getBillTypes } from '../../../utils/appUtils'
import {
  getAccountDetailsAction,
  getAccountDetailsResetAction,
} from '../../../store/bills'
import {
  fetchGSTINAction,
  fetchGSTINResetAction,
} from '../../../store/gstr'
import { useNavigate } from 'react-router-dom'
import ApplicationLinks from '../../../ApplicationLinks'

type AddAccountFields =
  | 'siteAddress'
  | 'siteAddress2'
  | 'billType'
  | 'ceilingArea'
  | 'ratePerSqft'
  | 'firstSlabDays'
  | 'firstSlabRate'
  | 'secondSlabDays'
  | 'secondSlabRate'
  | 'thirdSlabDays'
  | 'thirdSlabRate'
  | 'overDaysRate'
  | 'wallPercent'
  | 'openingBalance'
  | 'customer'
  | 'sitePlace'
  | 'sitePincode'
  | 'gstRate'
  | 'discount'
  | 'contactDetails'
  | 'siteEmail'
  | 'additionalInfo'
  | 'shipToGstin'
type AddAccountFieldsState = Record<
  AddAccountFields,
  Partial<{
    value: string
    error: boolean
    helperText: string
  }>
>

type IssueItemErrors = Record<AddAccountFields, Record<string, string>>

const issueItemErrors: IssueItemErrors = {
  ratePerSqft: {
    empty: 'Please enter rate',
  },
  siteAddress: {
    empty: 'Please enter site address',
  },
  siteAddress2: {
    empty: '',
  },
  billType: {
    empty: 'Please select bill type',
  },
  ceilingArea: {
    empty: 'Please enter plot area',
  },
  firstSlabDays: {
    empty: 'Please enter first slab days',
  },
  firstSlabRate: {
    empty: 'Please enter first slab rate',
  },
  secondSlabDays: {
    empty: 'Please enter second slab days',
  },
  secondSlabRate: {
    empty: 'Please enter second slab rate',
  },
  thirdSlabDays: {
    empty: 'Please enter third slab days',
  },
  thirdSlabRate: {
    empty: 'Please enter third slab rate',
  },
  overDaysRate: {
    empty: 'Please enter over days rate',
  },
  wallPercent: {
    empty: 'Please enter wall percentage',
  },
  openingBalance: {
    empty: 'Please enter opening balance',
  },
  customer: {
    empty: 'Please select party',
  },
  sitePlace: {
    empty: 'Please enter site place',
  },
  sitePincode: {
    empty: 'Please enter site pincode',
  },
  gstRate: {
    empty: 'Please enter GST rate',
  },
  discount: {
    empty: 'Please enter discount',
  },
  contactDetails: {
    empty: '',
  },
  siteEmail: {
    empty: '',
  },
  additionalInfo: {
    empty: '',
  },
  shipToGstin: {
    empty: '',
  },
}

const initialAddAccountFieldsState: AddAccountFieldsState = {
  ratePerSqft: {
    error: false,
    helperText: '',
    value: '',
  },
  siteAddress: {
    error: false,
    helperText: '',
    value: '',
  },
  siteAddress2: {
    error: false,
    helperText: '',
    value: '',
  },
  billType: {
    error: false,
    helperText: '',
    value: '',
  },
  ceilingArea: {
    error: false,
    helperText: '',
    value: null,
  },
  firstSlabDays: {
    error: false,
    helperText: '',
    value: '',
  },
  firstSlabRate: {
    error: false,
    helperText: '',
    value: '',
  },
  secondSlabDays: {
    error: false,
    helperText: '',
    value: '',
  },
  secondSlabRate: {
    error: false,
    helperText: '',
    value: '',
  },
  thirdSlabDays: {
    error: false,
    helperText: '',
    value: '',
  },
  thirdSlabRate: {
    error: false,
    helperText: '',
    value: '',
  },
  overDaysRate: {
    error: false,
    helperText: '',
    value: '',
  },
  wallPercent: {
    error: false,
    helperText: '',
    value: null,
  },
  openingBalance: {
    error: false,
    helperText: '',
    value: null,
  },
  customer: {
    error: false,
    helperText: '',
    value: '',
  },
  sitePlace: {
    error: false,
    helperText: '',
    value: '',
  },
  sitePincode: {
    error: false,
    helperText: '',
    value: '',
  },
  gstRate: {
    error: false,
    helperText: '',
    value: '',
  },
  discount: {
    error: false,
    helperText: '',
    value: null,
  },
  contactDetails: {
    error: false,
    helperText: '',
    value: '',
  },
  siteEmail: {
    error: false,
    helperText: '',
    value: '',
  },
  additionalInfo: {
    error: false,
    helperText: '',
    value: '',
  },
  shipToGstin: {
    error: false,
    helperText: '',
    value: '',
  },
}

const itemReducer = (state: Partial<AddAccountFieldsState>, action) => {
  const {
    type,
    payload: { name, value },
    helperText,
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: helperText
            ? helperText
            : initialAddAccountFieldsState[name]?.helperText,
        },
      }
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: issueItemErrors[name]['empty'],
        },
      }
    case 'RESET':
      return {
        ...initialAddAccountFieldsState,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: initialAddAccountFieldsState[name]?.helperText,
        },
      }
    default:
      return state
  }
}

export interface EditSiteViewProps {
  /** Current site (account) row — aliased internally to avoid clashing with `bill` state. */
  siteBill: Bill
  billId: number
}

/** Slips Site Info tab: same edit-site logic as the header dialog, without modifying AddNewAccountItem. */
export const EditSiteView: FC<EditSiteViewProps> = ({ siteBill, billId }) => {
  const partialBill = siteBill
  const customer: Customer | undefined = undefined
  const dispatchAction = useAppDispatch()
  const { data: userData } = useAppSelector((state) => state.userData)
  const [accountType, setAccountType] = useState<{ label: string; value: 'ITEMS_GIVEN' | 'ITEMS_TAKEN' }>({ label: 'Rent Out (to Party)', value: 'ITEMS_GIVEN' });

  const {
    loading: editBillLoading,
    success: addAccountSuccess,
    error: addAccountError,
    data: billData,
    message,
    code,
  } = useAppSelector((state) => state.editBill)
  const ADD_NEW_CUSTOMER = 'New Party'
  const [customerOptions, setCustomerOption] = useState(null)
  const [billTypes, setBillTypes] = useState<BillType[]>(
    partialBill ? getBillTypes(userData, partialBill) : []
  )
  const [billTypeIndex, setBillTypeIndex] = useState<number>(0)
  const [stateOption, setStateOption] = useState<StateOption | null>(null)

  const [showPlotArea, setShowPlotArea] = useState<boolean>(false)
  const [showRecurringCheckbox, setShowRecurringCheckbox] =
    useState<boolean>(false)
  const [show30DayMonth, setShow30DayMonth] = useState<boolean>(false)

  const [showWallPercent, setShowWallPercent] = useState<boolean>(false)
  const [showRate, setShowRate] = useState<boolean>(false)
  const [bill, setBill] = useState<Bill | null>(partialBill)

  const [itemData, dispatch] = useReducer(
    itemReducer,
    initialAddAccountFieldsState
  )
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(
    null
  )
  const [selectedBillType, setSelectedBillType] = useState<BillType | null>(
    null
  )
  const [gstEnabled, setGSTEnabled] = useState('no')
  const [checked, setChecked] = useState<boolean>(true)
  const [thirtyDaysMonthChecked, setThirtyDaysMonthChecked] =
    useState<boolean>(false)
  const [sezSupplyChecked, setSezSupplyChecked] = useState<boolean>(false)

  const [storecName, setStoreName] = useState(null)
  const isEditMode = partialBill
  const navigate = useNavigate()
  const [showProfileDialog, setShowProfileDialog] = useState(false)
  const [profileDialogMessage, setProfileDialogMessage] = useState('')
  const onCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked)
  }

  const on30DaysCheckBoxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setThirtyDaysMonthChecked(event.target.checked)
  }

  const onSezSupplyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSezSupplyChecked(event.target.checked)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGSTEnabled((event.target as HTMLInputElement).value)
    if (event.target.value !== 'yes-igst') {
      setSezSupplyChecked(false)
    }
    if (userData?.store?.nonGSTName) {
      const storeName =
        event.target.value == 'no'
          ? userData?.store?.nonGSTName
          : userData?.store?.name
      setStoreName(storeName)
    }
    if(!bill) {
      if (event.target.value === 'yes' && userData?.store.stateCode && userData?.store.state ) {
        setStateOption({ code: userData?.store.stateCode, label: userData?.store.state })
      } else if(event.target.value === 'yes-igst') {
        setStateOption(null)
      }
    }
   
    if (event.target.value == 'no') {
      dispatch({
        type: 'SET',
        payload: { name: 'gstRate', value: '' },
      })
    } else {
      dispatch({
        type: 'SET',
        payload: { name: 'gstRate', value: userData?.gstRate?.value },
      })
    }
  }

  const {
    list: { data },
  } = useAppSelector((state) => state.manageCustomers)
  const {
    data: agreements,
    message: agreementsError,
    success,
  } = useAppSelector((state) => state.getAgreements)
  const { data: accountDetails, message: accountError } = useAppSelector(
    (state) => state.accountDetails
  )
  const {
    success: gstinSuccess,
    data: gstinData,
  } = useAppSelector((state) => state.fetchGSTINDetails)
 
  useEffect(() => {
    dispatchAction(fetchCustomersActionV2())
  }, [])

  useEffect(() => {
    setChecked(showRecurringCheckbox)
  }, [showRecurringCheckbox])

  useEffect(() => {
    let gst = gstEnabled ? getGstRate() : 0
    dispatch({
      type: 'SET',
      payload: { name: 'gstRate', value: gst },
    })
  }, [gstEnabled])

  useEffect(() => {
    if (userData) {
      setShowRecurringCheckbox(userData.config.isRecurringBillsEnabled)
      setShow30DayMonth(userData.config.is30DaysMonthEnabled)
    }
    if (userData) {
      setBillTypes(getBillTypes(userData, billData))
    }
  }, [userData, billData])

  useEffect(() => {
    if (!success && agreementsError) {
      dispatchAction(
        showAlertAction({
          message: agreementsError,
          severity: 'error',
        })
      )
    }
  }, [agreementsError])

  useEffect(() => {
    if (isEditMode) return
    if (selectedCustomer && selectedCustomer.label !== ADD_NEW_CUSTOMER) {
      dispatchAction(getAgreementsAction(selectedCustomer.id))
    } else {
      setSelectedAgreement(null)
    }
  }, [selectedCustomer, isEditMode])

  useEffect(() => {
    if (partialBill) {
      dispatchAction(getAccountDetailsAction(partialBill.id))
      dispatchAction(getAgreementsAction(partialBill.customer.id))
    }
  }, [partialBill])


  useEffect(() => {
    return () => {
      dispatchAction(getAccountDetailsResetAction())
      dispatchAction(editBillResetAction()) // To manage the Alert
    }
  }, [])

  useEffect(() => {
    if (accountDetails) {
      setBill(accountDetails)
    } else if (accountError) {
      dispatchAction(
        showAlertAction({ message: accountError, severity: 'error' })
      )
    }
  }, [accountDetails, accountError])

  useEffect(() => {
    if (gstinSuccess && gstinData) {
      if (gstinData.stateCode) {
        setStateOption(getState(String(gstinData.stateCode)))
      }
      if (gstinData.pinCode) {
        dispatch({ type: 'SET', payload: { name: 'sitePincode', value: gstinData.pinCode } })
      }
      if (gstinData.place) {
        dispatch({ type: 'SET', payload: { name: 'sitePlace', value: gstinData.place } })
      }
      dispatchAction(fetchGSTINResetAction())
    }
  }, [gstinSuccess, gstinData])

  useEffect(() => {
    if (bill?.state && bill?.stateCode) {
      setStateOption({ code: bill?.stateCode, label: bill?.state })
    }
    
    // Check if bill has slabRates to determine bill type
    let index = 0
   
    if (bill?.slabRates && Object.keys(bill.slabRates).length > 0) {
      // If slabRates exist, set to "Per Pc + Sqft Area + With Rate Slabs" (index 3)
      index = 3
    } else if (bill?.billType === 'PER_ITEM') {
      index = 0
    } else if (bill?.totalCeilingArea > 0) {
      index = bill?.isSqftFlatRate === true ? 2 : 1
    } else if (bill?.ratePerSqft > 0) {
      index = bill?.isSqftFlatRate === true ? 5 : 4
    }
    
    if (bill?.agreementId) {
      // Keep user-picked agreement intact while still hydrating initial value from APIs.
      if (selectedAgreement?.id && selectedAgreement.id !== bill.agreementId) {
        // no-op
      } else {
        const matchedAgreement = agreements?.find(
          (agr) => agr.id === bill.agreementId
        )
        const resolvedAgreement: Agreement = matchedAgreement ?? {
          id: bill.agreementId,
          agreementNumber: '',
          agreementTitle: bill?.agreementTitle ?? '',
        }
        if (
          selectedAgreement?.id !== resolvedAgreement.id ||
          selectedAgreement?.agreementNumber !== resolvedAgreement.agreementNumber ||
          selectedAgreement?.agreementTitle !== resolvedAgreement.agreementTitle
        ) {
          setSelectedAgreement(resolvedAgreement)
        }
      }
    } else if (!selectedAgreement?.id) {
      setSelectedAgreement(null)
    }
    setBillTypeIndex(index)
    onSelectChange(null, billTypes[index])
    setChecked(bill?.isRecurringEnabled === true)
    setThirtyDaysMonthChecked(bill?.is30DaysMonthEnabled === true)
   
    if(bill?.accountType === "ITEMS_TAKEN") {
      setAccountType({ label: 'Hire In (from Partner)', value: 'ITEMS_TAKEN' })
    } else {
      setAccountType( { label: 'Rent Out (to Party)', value: 'ITEMS_GIVEN' })
    }
    dispatch({
      type: 'SET',
      payload: { name: 'siteAddress', value: bill?.siteAddress },
    }),
      dispatch({
        type: 'SET',
        payload: { name: 'siteAddress2', value: bill?.siteAddress2 ?? '' },
      }),
      dispatch({
        type: 'SET',
        payload: { name: 'wallPercent', value: bill?.wallAreaPercentage },
      }),
      dispatch({
        type: 'SET',
        payload: { name: 'openingBalance', value: bill?.openingBalance },
      }),
      dispatch({
        type: 'SET',
        payload: { name: 'billType', value: billTypes[billTypeIndex] },
      }),
      dispatch({
        type: 'SET',
        payload: { name: 'ratePerSqft', value: bill?.ratePerSqft },
      }),
      dispatch({
        type: 'SET',
        payload: { name: 'ceilingArea', value: bill?.totalCeilingArea },
      })
    dispatch({
      type: 'SET',
      payload: { name: 'sitePlace', value: bill?.sitePlace },
    })
    dispatch({
      type: 'SET',
      payload: { name: 'sitePincode', value: bill?.sitePincode },
    })
    dispatch({
      type: 'SET',
      payload: { name: 'discount', value: bill?.discount },
    })
    dispatch({
      type: 'SET',
      payload: { name: 'contactDetails', value: bill?.contactDetails ?? '' },
    })
    dispatch({
      type: 'SET',
      payload: { name: 'siteEmail', value: bill?.siteEmail ?? '' },
    })
    dispatch({
      type: 'SET',
      payload: { name: 'additionalInfo', value: bill?.additionalInfo ?? '' },
    })
    dispatch({
      type: 'SET',
      payload: { name: 'shipToGstin', value: bill?.shipToGstin },
    })

    // Prefill slab rate fields if they exist
    if (bill?.slabRates) {
      dispatch({
        type: 'SET',
        payload: { name: 'firstSlabDays', value: bill?.slabRates?.firstSlabDays?.toString() },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'firstSlabRate', value: bill?.slabRates?.firstSlabRate?.toString() },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'secondSlabDays', value: bill?.slabRates?.secondSlabDays?.toString() },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'secondSlabRate', value: bill?.slabRates?.secondSlabRate?.toString() },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'thirdSlabDays', value: bill?.slabRates?.thirdSlabDays?.toString() },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'thirdSlabRate', value: bill?.slabRates?.thirdSlabRate?.toString() },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'overDaysRate', value: bill?.slabRates?.overDaysPerDayRate?.toString() },
      })
    }
    
    if (bill?.isGSTEnabled) {
      setGSTEnabled(bill?.gstType === 'IGST' ? 'yes-igst' : 'yes')
      dispatch({
        type: 'SET',
        payload: { name: 'gstRate', value: getGstRate() },
      })
    } else {
      setGSTEnabled('no')
    }
    setSezSupplyChecked(bill?.supplyNature === 'SEZ')
  }, [bill, dispatch, agreements, selectedAgreement?.id, selectedAgreement?.agreementNumber, selectedAgreement?.agreementTitle])

  function getGstRate() {
    // If bill object exists, always return rate from it (whether 0 or not)
    if (bill) {
      if(bill?.gstType === 'IGST') {
        return (bill?.igstRate ?? 0)
      } else {
        return (bill?.sgstRate ?? 0) + (bill?.cgstRate ?? 0)
      }
    }
    
    // Only use userData config when bill object doesn't exist
    return (userData?.config?.sgst ?? 0) + (userData?.config?.cgst ?? 0)
  }

  // Customer Select
  const onCustomerChange = (e, newValue: { id; label; name?; state?; address? }) => {
    if (!newValue) {
      setSelectedCustomer(null)
      setSelectedAgreement(null)
    } else if (newValue.label && newValue.label === ADD_NEW_CUSTOMER) {
      setSelectedCustomer(newValue)
      // New Party flow is only for add-site mode; not used when editing from slips tab.
      setSelectedAgreement(null)
    } else {
      const selected = {
        ...newValue,
        label: formatPartySelectedLabel(newValue.name, newValue.state),
      }
      setSelectedCustomer(selected)
      dispatch({
        type: 'SET',
        payload: { name: 'customer', value: selected.label },
      })
    }
  }

  const onChangeHandler = (e) => {
    const {
      target: { name, value },
    } = e
    if (
      name === 'customerMobile' ||
      name === 'customerAddress' ||
      name === 'customerGSTIN' ||
      name === 'contactDetails' ||
      name === 'siteEmail' ||
      name === 'additionalInfo' ||
      name === 'siteAddress2' ||
      name === 'shipToGstin'
    ) {
      const nextValue =
        name === 'contactDetails'
          ? String(value).slice(0, 150)
          : name === 'siteEmail'
          ? String(value).slice(0, 150)
          : name === 'additionalInfo'
          ? String(value).slice(0, 100)
          : value
      dispatch({ type: 'SET', payload: { name, value: nextValue } })
      if (name === 'shipToGstin' && isValidGST(value)) {
        dispatchAction(fetchGSTINAction(value))
      }
    } else if (value.length > 0) {
      dispatch({ type: 'SET', payload: { name, value } })
    } else {
      dispatch({ type: 'ERROR', payload: { name, value } })
    }
  }

  useEffect(() => {
    const addNewCustomer = { id: 0, label: ADD_NEW_CUSTOMER }
    const customerlist = data.map((customer) => {
      return {
        id: customer.id,
        label: formatPartyAutocompleteLabel(
          customer.name,
          customer.mobile,
          customer.address
        ),
        name: customer.name,
        state: customer.state,
        mobile: customer.mobile,
        address: customer.address,
      }
    })
    setCustomerOption([addNewCustomer, ...customerlist])
  }, [data])
  useEffect(() => {
    if (customer) {
      const option = {
        id: customer.id,
        label: formatPartySelectedLabel(customer.name, customer.state),
        name: customer.name,
        state: customer.state,
        mobile: customer.mobile,
        address: customer.address,
      }
      setSelectedCustomer(option)
    }
  }, [customer])

  useEffect(() => {
    setBillTypeIndex(0)
    onSelectChange(null, billTypes[0])
    dispatch({
      type: 'SET',
      payload: { name: 'billType', value: billTypes[billTypeIndex] },
    })
  }, [dispatch])

  useEffect(() => {
    if (addAccountSuccess) {
      dispatchAction(fetchCustomerAccountAction('OPEN'))
      dispatchAction(fetchSlipsListAction(billId))
      dispatchAction(editBillResetAction())
      dispatchAction(
        showAlertAction({
          message: 'Site added successfully',
          severity: 'success',
        })
      )
    } else if (addAccountError && message) {
      // Check if error code is 1101 (for both Add Site and Edit Site)
      if (code === 1101) {
        setProfileDialogMessage(message || 'Please update your business profile to continue.')
        setShowProfileDialog(true)
      } else {
        dispatchAction(showAlertAction({ message: message, severity: 'error' }))
      }
    }
  }, [addAccountSuccess, addAccountError, message, code, isEditMode, billId, dispatchAction])

  const onAgreementChange = (e: any, newValue: Agreement) => {
    setSelectedAgreement(newValue)
  }

  const onSelectChange = (e: any, newValue: BillType) => {
    setSelectedBillType(newValue)
    if (newValue) {
      dispatch({ type: 'SET', payload: { name: 'billType', value: newValue } })
      setShowRecurringCheckbox(
        userData.config.isRecurringBillsEnabled &&
          (newValue.index === 0 || newValue.index === 3)
      )

      setShow30DayMonth(
        userData.config.is30DaysMonthEnabled &&
          (newValue.index === 0 || newValue.index === 3)
      )

      if (newValue.index !== 2 && newValue.index !== 4) {
        dispatch({ type: 'SET', payload: { name: 'minDaysRent', value: '' } })
        dispatch({ type: 'SET', payload: { name: 'overDaysRate', value: '' } })
      }

      if (newValue.index === 0) {
        dispatch({ type: 'SET', payload: { name: 'ceilingArea', value: '' } })
        dispatch({ type: 'SET', payload: { name: 'wallPercent', value: '' } })
        dispatch({ type: 'SET', payload: { name: 'ratePerSqft', value: '' } })
        setShowPlotArea(false)
        setShowRate(false)
        setShowWallPercent(false)
      } else if (newValue.index < 3) {
        dispatch({ type: 'SET', payload: { name: 'wallPercent', value: '' } })
        setShowPlotArea(true)
        setShowRate(true)
        setShowWallPercent(false)
      } else if(newValue.index === 3) {
        dispatch({ type: 'SET', payload: { name: 'ceilingArea', value: '' } })
        dispatch({ type: 'SET', payload: { name: 'ratePerSqft', value: '' } })
        setShowPlotArea(false)
        setShowRate(false)
        setShowWallPercent(false)
      } else {
        dispatch({ type: 'SET', payload: { name: 'ceilingArea', value: '' } })
        setShowPlotArea(false)
        setShowRate(true)
        setShowWallPercent(true)
      }
    }
  }

  const submitHandle = () => {
    let error = false
    if (!isEditMode && !selectedCustomer) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'customer', value: null } })
    }
   
    if (!itemData?.siteAddress?.value) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'siteAddress', value: null } })
    }

    if (!selectedBillType) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'billType', value: null } })
    }
    const rate = itemData?.ratePerSqft?.value
      ? Number(itemData?.ratePerSqft?.value)
      : 0
    // Slab rate validations - only for specific bill type
    const firstSlabDays = selectedBillType?.index === 3 && itemData?.firstSlabDays?.value ? Number(itemData?.firstSlabDays?.value) : null
    const firstSlabRate = selectedBillType?.index === 3 && itemData?.firstSlabRate?.value ? Number(itemData?.firstSlabRate?.value) : null
    const secondSlabDays = selectedBillType?.index === 3 && itemData?.secondSlabDays?.value ? Number(itemData?.secondSlabDays?.value) : null
    const secondSlabRate = selectedBillType?.index === 3 && itemData?.secondSlabRate?.value ? Number(itemData?.secondSlabRate?.value) : null
    const thirdSlabDays = selectedBillType?.index === 3 && itemData?.thirdSlabDays?.value ? Number(itemData?.thirdSlabDays?.value) : null
    const thirdSlabRate = selectedBillType?.index === 3 && itemData?.thirdSlabRate?.value ? Number(itemData?.thirdSlabRate?.value) : null
    const overDaysRate = selectedBillType?.index === 3 && itemData?.overDaysRate?.value ? Number(itemData?.overDaysRate?.value) : null

    // Validate first slab is required for slab rate bill type
    if (selectedBillType?.index === 3 && (!firstSlabDays || !firstSlabRate)) {
      error = true
      if (!firstSlabDays) {
        dispatch({ type: 'ERROR', payload: { name: 'firstSlabDays', value: null } })
      }
      if (!firstSlabRate) {
        dispatch({ type: 'ERROR', payload: { name: 'firstSlabRate', value: null } })
      }
    }

    const plotArea = itemData?.ceilingArea?.value
      ? Number(itemData?.ceilingArea?.value)
      : 0
    const wallPercent = itemData?.wallPercent?.value
      ? Number(itemData?.wallPercent?.value)
      : 0
    const openingBalance = itemData?.openingBalance?.value
      ? Number(itemData?.openingBalance?.value)
      : 0

    // Validate plot area for bill types that need it
    if (showPlotArea && plotArea === 0) {
      error = true
      dispatch({ type: 'SET', payload: { name: 'ceilingArea', value: '' } })
      dispatch({ type: 'ERROR', payload: { name: 'ceilingArea', value: '' } })
    }
    if (showRate && rate === 0) {
      error = true
      dispatch({ type: 'SET', payload: { name: 'ratePerSqft', value: '' } })
      dispatch({ type: 'ERROR', payload: { name: 'ratePerSqft', value: '' } })
    }
    if (gstEnabled && (itemData?.gstRate?.value === undefined || itemData?.gstRate?.value === '') ) {
      error = true
      dispatch({ type: 'SET', payload: { name: 'gstRate', value: '' } })
      dispatch({ type: 'ERROR', payload: { name: 'gstRate', value: '' } })
    }
    const shipToGstinValue = (itemData?.shipToGstin?.value ?? '').toString().trim()
    if (gstEnabled !== 'no' && shipToGstinValue && !isValidGST(shipToGstinValue)) {
      error = true
      dispatch({
        type: 'ERROR',
        payload: { name: 'shipToGstin', value: itemData?.shipToGstin?.value },
      })
    }

    if (!error) {
       const gstRate = itemData?.gstRate?.value
      ? Number(itemData?.gstRate?.value)
      : 0
      const billType =
        userData.config?.isMixTypeAllowed || selectedBillType.index == 0
          ? 'PER_ITEM'
          : 'PER_SQFT'
      const isFlatRate =
        selectedBillType.index == 2 || selectedBillType.index == 5
      const sitePlace =
        gstEnabled && itemData?.sitePlace?.value
          ? itemData?.sitePlace?.value
          : null
      const sitePincode =
        gstEnabled && itemData?.sitePincode?.value
          ? itemData?.sitePincode?.value
          : null

      // Create SlabRates object - only for specific bill type
      const slabRates = selectedBillType?.index === 3 ? {
        ...(isEditMode && bill?.slabRates?.id && { id: bill.slabRates.id }),
        firstSlabDays: firstSlabDays,
        firstSlabRate: firstSlabRate,
        secondSlabDays: secondSlabDays,
        secondSlabRate: secondSlabRate,
        thirdSlabDays: thirdSlabDays,
        thirdSlabRate: thirdSlabRate,
        overDaysPerDayRate: overDaysRate,
        isSqftBased: false
      } : null

      const apiRequest = {
        billId: partialBill?.id,
        date: isEditMode ? null : getFormattedDateTimeIST(new Date()),
        customerId: isEditMode ? bill?.customer.id : selectedCustomer.id,
        siteAddress: itemData?.siteAddress?.value,
        siteAddress2: itemData?.siteAddress2?.value ?? '',
        billType: billType,
        ratePerSqft: selectedBillType?.index === 3 ? 0 : rate,
        ceilingArea: plotArea,
        wallPercent: wallPercent,
        isSqftFlatRate: isFlatRate,
        isGSTEnabled: gstEnabled !== 'no',
        enteries: [],
        isRecurringEnabled: showRecurringCheckbox && checked,
        agreementId: selectedAgreement?.id ?? bill?.agreementId,
        stateCode: stateOption?.code,
        state: stateOption?.label,
        sitePlace: sitePlace,
        sitePincode: sitePincode ? Number(sitePincode) : null,
        shipToGstin: gstEnabled !== 'no' && shipToGstinValue ? shipToGstinValue : '',
        is30DaysMonthEnabled: show30DayMonth && thirtyDaysMonthChecked,
        gstType:
          gstEnabled === 'yes'
            ? 'CGST_SGST'
            : gstEnabled === 'yes-igst'
            ? 'IGST'
            : 'NONE',
        gstRate: gstRate,
        supplyNature:
          gstEnabled === 'yes-igst' && sezSupplyChecked ? 'SEZ' : 'REGULAR',
        openingBalance: openingBalance,
        accountType: accountType.value,
        contactDetails: String(itemData?.contactDetails?.value ?? '').slice(
          0,
          150
        ),
        siteEmail: String(itemData?.siteEmail?.value ?? ''),
        additionalInfo: String(itemData?.additionalInfo?.value ?? '').slice(
          0,
          100
        ),
        ...(slabRates && { slabRates }),
        ...(isEditMode && bill?.discount != null && Number(bill.discount) > 0 && {
          discount: itemData?.discount?.value != null && itemData?.discount?.value !== ''
            ? Number(itemData.discount.value)
            : Number(bill.discount),
        }),
      }
      dispatchAction(updateBillAction(apiRequest))
    }
  }

  useEffect(() => {
    setStoreName(userData?.store?.nonGSTName)
  }, [userData?.store])

  const handleChangeStoreName = (e) => {
    const gstEnabled =
      e.target.value == userData?.store?.nonGSTName ? 'no' : 'yes'
    setStoreName(e.target.value)
    setGSTEnabled(gstEnabled)
  }
  useEffect(() => {
    return () => {
      dispatchAction(editBillResetAction()) // To manage the Alert
    }
  }, [customerOptions])

  const defaultProps = {
    options: agreements ? agreements : [],
    getOptionLabel: (option: Agreement) =>
      option.agreementTitle && option.agreementNumber
        ? `${option.agreementNumber} ${option.agreementTitle}`
        : option.agreementTitle
          ? option.agreementTitle
          : option.agreementNumber
            ? `AGREEMENT: ${option.agreementNumber}`
            : 'AGREEMENT',
  }

  const accountFormAndProfile = (
    <>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12} m={2}>
          <Box
            component="form"
            sx={{
              '& .MuiTextField-root': { m: 0 },
            }}
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={3}>
              {/* Left column: address fields, contact, recurring */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      name="siteAddress"
                      onChange={onChangeHandler}
                      value={itemData.siteAddress.value}
                      label="Site/Shipping Address"
                      type="string"
                      fullWidth
                      required
                      error={itemData['siteAddress']['error']}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      name="siteAddress2"
                      onChange={onChangeHandler}
                      value={itemData.siteAddress2?.value ?? ''}
                      label="Address Line 2"
                      type="string"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomNumberField
                      name="openingBalance"
                      onChange={onChangeHandler}
                      value={itemData.openingBalance?.value}
                      label="Opening Balance(Optional)"
                      fullWidth
                      maxLength={10}
                      InputLabelProps={{
                        shrink:
                          itemData.openingBalance?.value !== null &&
                          itemData.openingBalance?.value !== undefined,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      name="contactDetails"
                      onChange={onChangeHandler}
                      value={itemData.contactDetails?.value ?? ''}
                      label="Contact Details"
                      type="text"
                      fullWidth
                      inputProps={{ maxLength: 150 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      name="siteEmail"
                      onChange={onChangeHandler}
                      value={itemData.siteEmail?.value ?? ''}
                      label="Email Address"
                      type="email"
                      fullWidth
                      inputProps={{ maxLength: 150 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      name="additionalInfo"
                      onChange={onChangeHandler}
                      value={itemData.additionalInfo?.value ?? ''}
                      label="Additional Info"
                      type="text"
                      fullWidth
                      multiline
                      rows={3}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Right column: party, bill type, agreement+account type, opening balance */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {!isEditMode && (
                    <Grid item xs={12}>
                      <Autocomplete
                        size="small"
                        id="select-customer"
                        value={selectedCustomer}
                        options={customerOptions}
                        onChange={onCustomerChange}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        getOptionLabel={(option) => option?.label ?? ''}
                        renderInput={(params) => (
                          <TextField
                            size="small"
                            {...params}
                            label="Select Party"
                            required
                            error={itemData['customer']['error']}
                          />
                        )}
                        renderOption={(props, option) => {
                          if (option.label === ADD_NEW_CUSTOMER) {
                            return (
                              <Typography
                                {...props}
                                noWrap
                                style={{
                                  color: 'green',
                                  height: 40,
                                  marginRight: 10,
                                }}
                              >
                                {option.label}
                              </Typography>
                            )
                          }
                          const secondary = formatPartyOptionSecondaryLabel(
                            option.mobile,
                            option.address
                          )
                          return (
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                display: 'flex !important',
                                flexDirection: 'column',
                                alignItems: 'flex-start !important',
                                py: 1,
                                mr: 1,
                              }}
                            >
                              <Typography noWrap sx={{ width: '100%' }}>
                                {formatPartySelectedLabel(
                                  option.name,
                                  option.state
                                )}
                              </Typography>
                              {secondary ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ width: '100%' }}
                                >
                                  {secondary}
                                </Typography>
                              ) : null}
                            </Box>
                          )
                        }}
                      />
                      {selectedCustomer?.address &&
                        selectedCustomer.label !== ADD_NEW_CUSTOMER && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {selectedCustomer.address}
                          </Typography>
                        )}
                    </Grid>
                  )}
                  {storecName && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="store-name-label">Store Name</InputLabel>
                        <Select
                          size="small"
                          labelId="store-name-label"
                          id="store-name"
                          value={storecName}
                          name={'store-name'}
                          label="Business Name"
                          onChange={handleChangeStoreName}
                        >
                          <MenuItem value={userData?.store?.nonGSTName}>
                            {userData?.store?.nonGSTName}
                          </MenuItem>
                          <MenuItem value={userData?.store?.name}>
                            {userData?.store?.name}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {isEditMode && bill?.discount != null && Number(bill.discount) > 0 && (
                    <Grid item xs={12}>
                      <CustomNumberField
                        name="discount"
                        onChange={onChangeHandler}
                        value={itemData.discount?.value}
                        label="Site level discount"
                        fullWidth
                        maxLength={10}
                        InputLabelProps={{
                          shrink:
                            itemData.discount?.value !== null &&
                            itemData.discount?.value !== undefined &&
                            itemData.discount?.value !== '',
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      disablePortal
                      id="bill_type_options"
                      value={selectedBillType}
                      options={billTypes}
                      onChange={onSelectChange}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          label="Select bill type"
                          required
                          error={itemData['billType']['error']}
                          sx={{ minWidth: '100%', margin: 0 }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const isDeprecated = option.label.includes('Deprecated')
                        return (
                          <Grid component={'li'} container {...props}>
                            <Grid
                              id={option.label}
                              data-cy={option.label}
                              item
                              xs
                              pl={1}
                              pr={1}
                              pt={0.7}
                              pb={0.7}
                              sx={{
                                color: isDeprecated ? 'warning.main' : 'inherit',
                              }}
                            >
                              {option.label}
                            </Grid>
                          </Grid>
                        )
                      }}
                    />
                  </Grid>
                  {showPlotArea && (
                    <Grid item xs={12}>
                      <CustomNumberField
                        onChange={onChangeHandler}
                        name="ceilingArea"
                        value={itemData.ceilingArea.value}
                        label="Sqft Area"
                        error={itemData['ceilingArea']['error']}
                        fullWidth
                        maxLength={8}
                      />
                    </Grid>
                  )}
                  {selectedBillType?.index === 3 && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 0.2, fontWeight: 'bold' }}>
                          Sqft Rates
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <CustomNumberField
                          name="firstSlabDays"
                          onChange={onChangeHandler}
                          value={itemData.firstSlabDays?.value}
                          label="First X Days"
                          error={itemData['firstSlabDays']['error']}
                          maxLength={3}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <CustomNumberField
                          name="secondSlabDays"
                          onChange={onChangeHandler}
                          value={itemData.secondSlabDays?.value}
                          label="First Y Days"
                          maxLength={3}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <CustomNumberField
                          name="thirdSlabDays"
                          onChange={onChangeHandler}
                          value={itemData.thirdSlabDays?.value}
                          label="First Z Days"
                          maxLength={3}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <CustomNumberField
                          name="firstSlabRate"
                          onChange={onChangeHandler}
                          value={itemData.firstSlabRate?.value}
                          label={
                            itemData.firstSlabDays?.value
                              ? `Rate for ${itemData.firstSlabDays.value} Days`
                              : 'Rate for X Days'
                          }
                          error={itemData['firstSlabRate']['error']}
                          maxLength={6}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <CustomNumberField
                          name="secondSlabRate"
                          onChange={onChangeHandler}
                          value={itemData.secondSlabRate?.value}
                          label={
                            itemData.secondSlabDays?.value
                              ? `Rate for ${itemData.secondSlabDays.value} Days`
                              : 'Rate for Y Days'
                          }
                          maxLength={6}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <CustomNumberField
                          name="thirdSlabRate"
                          onChange={onChangeHandler}
                          value={itemData.thirdSlabRate?.value}
                          label={
                            itemData.thirdSlabDays?.value
                              ? `Rate for ${itemData.thirdSlabDays.value} Days`
                              : 'Rate for Z Days'
                          }
                          maxLength={6}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomNumberField
                          name="overDaysRate"
                          onChange={onChangeHandler}
                          value={itemData.overDaysRate?.value}
                          label="Extra days rate(/sqft/day)"
                          maxLength={6}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}
                  {showWallPercent && (
                    <Grid item xs={12}>
                      <CustomNumberField
                        name="wallPercent"
                        onChange={onChangeHandler}
                        value={itemData.wallPercent.value}
                        label="Wall Percent(Optional)"
                        fullWidth
                        maxLength={2}
                      />
                    </Grid>
                  )}
                  {showRate && (
                    <Grid item xs={12}>
                      <CustomNumberField
                        name="ratePerSqft"
                        onChange={onChangeHandler}
                        value={itemData.ratePerSqft?.value}
                        label="Rate Per Sqft"
                        error={itemData['ratePerSqft']['error']}
                        maxLength={4}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {show30DayMonth && (
                    <Grid item xs={12}>
                      <Checkbox
                        checked={thirtyDaysMonthChecked}
                        onChange={on30DaysCheckBoxChange}
                        sx={{ ml: -1 }}
                      />{' '}
                      Enable 30 days month
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Autocomplete
                      size="small"
                      {...defaultProps}
                      disablePortal
                      id="combo-box-demo"
                      value={selectedAgreement}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      onChange={onAgreementChange}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          label="Select Agreement"
                          sx={{ minWidth: '100%', margin: 0 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      size="small"
                      disabled={!!isEditMode}
                      disablePortal
                      id="account-type-autocomplete"
                      value={accountType}
                      options={[
                        { label: 'Rent Out (to Party)', value: 'ITEMS_GIVEN' },
                        { label: 'Hire In (from Partner)', value: 'ITEMS_TAKEN' },
                      ]}
                      onChange={(e, newValue) => {
                        if (newValue) {
                          setAccountType(
                            newValue as {
                              label: string
                              value: 'ITEMS_GIVEN' | 'ITEMS_TAKEN'
                            }
                          )
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Account Type"
                          required
                          fullWidth
                        />
                      )}
                      renderOption={(props, option) => (
                        <Grid component="li" container {...props}>
                          <Grid item xs pl={1} pr={1} pt={0.7} pb={0.7}>
                            {option.label}
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                  {showRecurringCheckbox && (
                    <Grid item xs={12}>
                      <Checkbox
                        checked={checked}
                        onChange={onCheckBoxChange}
                        sx={{ ml: -1 }}
                      />{' '}
                      Recurring Bills (Monthly/Fortnightly/Weekly)
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Full-width GST section */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel id="demo-controlled-radio-buttons-group">
                        Apply GST on this account?
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="demo-controlled-radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={gstEnabled}
                        onChange={handleChange}
                        row
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes (SGST+CGST)"
                          data-cy="gst_radio_sgst_cgst"
                        />
                        <FormControlLabel
                          value="yes-igst"
                          control={<Radio />}
                          label="Yes (IGST)"
                          data-cy="gst_radio_igst"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No GST"
                          data-cy="gst_radio_no"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {gstEnabled !== 'no' && (
                    <Grid item xs={3}>
                      <TextField
                        size="small"
                        name="sitePlace"
                        onChange={onChangeHandler}
                        value={itemData.sitePlace?.value ? itemData.sitePlace?.value : ''}
                        label="Place (Optional)"
                        fullWidth
                      />
                    </Grid>
                  )}
                  {gstEnabled !== 'no' && (
                    <Grid item xs={3}>
                      <CustomNumberField
                        name="sitePincode"
                        onChange={onChangeHandler}
                        value={
                          itemData.sitePincode?.value
                            ? itemData.sitePincode?.value
                            : ''
                        }
                        label="Site pincode"
                        fullWidth
                        maxLength={6}
                        inputProps={{
                          'data-cy': 'site_pincode_input',
                        }}
                      />
                    </Grid>
                  )}
                  {gstEnabled !== 'no' && (
                    <Grid item xs={gstEnabled === 'yes-igst' ? 3 : 6}>
                      <TextField
                        size="small"
                        name="shipToGstin"
                        onChange={onChangeHandler}
                        value={
                          itemData.shipToGstin?.value ? itemData.shipToGstin?.value : ''
                        }
                        label="Ship To GSTIN (Optional)"
                        fullWidth
                        error={itemData['shipToGstin']?.['error']}
                        helperText={itemData['shipToGstin']?.['error'] ? 'Invalid GSTIN format' : ''}
                        inputProps={{
                          'data-cy': 'ship_to_gstin_input',
                        }}
                      />
                    </Grid>
                  )}
                  {gstEnabled === 'yes-igst' && (
                    <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={sezSupplyChecked}
                        onChange={onSezSupplyChange}
                        sx={{ ml: -1 }}
                      />{' '}
                      SEZ Supply
                    </Grid>
                  )}
                  {gstEnabled !== 'no' && (
                    <Grid item xs={6}>
                      <SelectStateItem
                        selectedStateOption={stateOption}
                        label={'State'}
                        onChange={(option) => {
                          setStateOption(option)
                        }}
                      />
                    </Grid>
                  )}
                  {gstEnabled !== 'no' && (
                    <Grid item xs={6}>
                      <CustomNumberField
                        name="gstRate"
                        onChange={onChangeHandler}
                        value={itemData.gstRate?.value ?? ''}
                        label="GST Rate(%)"
                        fullWidth
                        maxLength={6}
                        inputProps={{
                          'data-cy': 'gst_rate_input',
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>{' '}
      </Grid>

      {/* Confirm Dialog for Profile Update - Error 1101 */}
      <Dialog
        open={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
        aria-labelledby="profile-dialog-title"
        aria-describedby="profile-dialog-description"
      >
        <DialogTitle id="profile-dialog-title">
          Update Business Profile Required
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="profile-dialog-description"
            sx={{ color: 'error.main' }}
          >
            {profileDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProfileDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Navigate to Business Profile with state
              // Only set reopenAddSite for Add Site mode, not Edit Site
              navigate(ApplicationLinks.profileDetailLink(), {
                state: { 
                  reopenAddSite: !isEditMode, 
                  fromAddSite: true,
                  isEditMode: isEditMode 
                },
              })
              
              // Close the dialog
              setShowProfileDialog(false)
            }}
            color="primary"
            variant="contained"
          >
            Edit Profile
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  return (
    <Box
      sx={{
        maxWidth: 1200,
        my:2,
        mx: 'auto',
        width: '100%',
        px: 2,
        height: 'calc(100vh - 260px)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'visible',
      }}
    >
      <Box sx={{ flex: 1 }}>
        {accountFormAndProfile}
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          pt: 1.5,
          pb: 1,
          mr:2,
          bgcolor: 'background.paper',
        }}
      >
        <LoadingButton
          variant="contained"
          sx={{ backgroundColor: 'green' }}
          size="large"
          loading={editBillLoading}
          onClick={() => void submitHandle()}
        >
          SAVE INFO
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default EditSiteView
